import { useMutation, useQueryClient } from '@tanstack/react-query';

import { JobApplication } from '@restworld/utility-types';
import { UpdateJobApplicationParams, useWorkerJobPositionsService } from '@restworld/data-services';

import {
  useDiscardJobApplicationQueryKey,
  useEmployerJobApplicationQueryKey,
  usePreselectionRejectedJobApplicationsQueryKey,
} from './use-employer-job-applications';

// The job application state machine is idempotent: PATCHing an application to the
// status it already holds returns 400 with this message. That's not a real failure
// for the employer — the candidate is already in the target status (e.g. they hit
// "set interview" twice, or a previous request moved the status but errored after),
// so we treat it as success and follow the happy path instead of surfacing an error.
const ALREADY_IN_TARGET_STATUS_MESSAGE = 'Lo stato attuale è già impostato come';

function isAlreadyInTargetStatusError(error: unknown): boolean {
  const err = error as { code?: number; data?: { error?: string } };
  return (
    err?.code === 400 &&
    typeof err?.data?.error === 'string' &&
    err.data.error.startsWith(ALREADY_IN_TARGET_STATUS_MESSAGE)
  );
}

function useUpdateJobApplication() {
  const queryKey = useEmployerJobApplicationQueryKey();
  const discardedQueryKey = useDiscardJobApplicationQueryKey();
  const preselectionRejectedQueryKey = usePreselectionRejectedJobApplicationsQueryKey();

  const workerJobpositionsService = useWorkerJobPositionsService();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    async mutationFn(params: UpdateJobApplicationParams) {
      try {
        return await workerJobpositionsService.updateJobApplication(params);
      } catch (error) {
        // Candidate is already in the requested status → treat as success (keep the
        // optimistic update, refetch) rather than rolling back and showing an error.
        if (isAlreadyInTargetStatusError(error)) return null;
        throw error;
      }
    },
    onMutate(variables: UpdateJobApplicationParams) {
      queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData(queryKey) as JobApplication[];
      // Optimistically update the cache with the new value
      queryClient.setQueryData(queryKey, (oldData: JobApplication[]) => {
        if (variables.params.status_key === 'worker_jp_discarded') {
          return oldData.filter((jobApplication) => jobApplication.job_position_id !== variables.jobPositionId);
        }
        return oldData.map((jobApplication) => {
          if (jobApplication.worker_id === variables.workerId) {
            return {
              ...jobApplication,
              ...variables.params,
            };
          }
          return jobApplication;
        });
      });
      // when user discard the candidate, we need to add it to the discarded job application list
      if (variables.params.status_key === 'worker_jp_discarded') {
        const discardedCandidate = prevData?.find((jobApplication) => jobApplication.worker_id === variables.workerId);
        queryClient.setQueryData(discardedQueryKey, (oldData: JobApplication[]) => [
          ...oldData,
          ...(discardedCandidate
            ? [
                {
                  ...discardedCandidate,
                  ...variables.params,
                },
              ]
            : []),
        ]);
      }
      // Return the previous value in case of rollback
      return { prevData };
    },
    onSuccess() {
      // Invalidate each list separately. Passing an array of query keys as a
      // single `queryKey` nests them and matches nothing, so the lists never
      // refetch without a manual reload. preselection_rejected is included so a
      // promoted "non idoneo" leaves that list and the pipeline picks it up.
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: discardedQueryKey });
      queryClient.invalidateQueries({ queryKey: preselectionRejectedQueryKey });
    },
    onError: (_err, prevApplication, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(queryKey, context.prevData);
      }
    },
  });
  return mutation;
}

export function useUpdateDiscardedJobApplication() {
  const queryKey = useDiscardJobApplicationQueryKey();
  const queryKeyJobApplication = useEmployerJobApplicationQueryKey();

  const workerJobpositionsService = useWorkerJobPositionsService();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (params: UpdateJobApplicationParams) => workerJobpositionsService.updateJobApplication(params),
    onMutate(variables: UpdateJobApplicationParams) {
      queryClient.cancelQueries({ queryKey });
      const prevData = queryClient.getQueryData(queryKey) as JobApplication[];
      // Optimistically update the cache with the new value
      queryClient.setQueryData(queryKey, (oldData: JobApplication[]) =>
        oldData.filter((jobApplication) => jobApplication.worker_id !== variables.workerId)
      );
      // when user re add the discarded candidate, we need to add it back to the job application list
      if (variables.params.status_key === 'worker_jp_employer_interested') {
        const reAddedCandidate = prevData?.find((jobApplication) => jobApplication.worker_id === variables.workerId);
        queryClient.setQueryData(queryKeyJobApplication, (oldData: JobApplication[]) => {
          return [
            ...oldData,
            ...(reAddedCandidate
              ? [
                  {
                    ...reAddedCandidate,
                    ...variables.params,
                  },
                ]
              : []),
          ];
        });
      }
      // Return the previous value in case of rollback
      return { prevData };
    },
    onSuccess() {
      // Invalidate each list separately; an array of keys as a single `queryKey`
      // nests them and matches nothing (so nothing refetched without a reload).
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: queryKeyJobApplication });
    },
    onError: (_err, prevApplication, context) => {
      if (context?.prevData) {
        queryClient.setQueryData(queryKey, context.prevData);
      }
    },
  });
  return mutation;
}
export default useUpdateJobApplication;

export function useUpdateJobApplicationMutation() {
  const workerJobpositionsService = useWorkerJobPositionsService();

  const mutation = useMutation({
    mutationFn: (params: UpdateJobApplicationParams) => workerJobpositionsService.updateJobApplication(params),
  });
  return mutation;
}
